/**
 * Dialog de configuração do node Google Analytics 4
 * Permite configurar nome do evento e parâmetros key/value
 * 
 * RELEASE: 1.1.0 - 2026-03-08 - Adicionado botão de ajuda contextual (NodeHelpDialog)
 * RELEASE: 1.0.0 - 2026-03-08 - Novo dialog de rastreamento GA4
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, BarChart3, HelpCircle } from 'lucide-react';
import { FlowNode } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '@/components/tenant/flows/NodeIdField';
import { NodeHelpDialog } from './NodeHelpDialog';

interface ConfigureGA4DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, any>) => void;
}

/**
 * Dialog para configurar evento GA4 com parâmetros dinâmicos
 */
export function ConfigureGA4Dialog({ open, onOpenChange, node, onSave }: ConfigureGA4DialogProps) {
  const config = node.data.config || {};
  const [eventName, setEventName] = useState<string>(config.event_name || '');
  const [params, setParams] = useState<Array<{ key: string; value: string }>>(
    Object.entries((config.params as Record<string, string>) || {}).map(([key, value]) => ({ key, value }))
  );
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  /** Controle do modal de ajuda */
  const [showHelp, setShowHelp] = useState(false);

  /** Adiciona par chave/valor */
  const addParam = () => {
    setParams(prev => [...prev, { key: '', value: '' }]);
  };

  /** Remove par pelo índice */
  const removeParam = (index: number) => {
    setParams(prev => prev.filter((_, i) => i !== index));
  };

  /** Atualiza chave ou valor */
  const updateParam = (index: number, field: 'key' | 'value', val: string) => {
    setParams(prev => prev.map((v, i) => i === index ? { ...v, [field]: val } : v));
  };

  /** Salva configuração */
  const handleSave = () => {
    const paramsRecord: Record<string, string> = {};
    params.forEach(p => {
      if (p.key.trim()) paramsRecord[p.key.trim()] = p.value;
    });

    onSave(node.id, {
      ...config,
      event_name: eventName,
      params: paramsRecord,
      nickname: nicknameRef.current?.getNickname(),
    });
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-500" />
              Google Analytics 4
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node + Apelido */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node.id}
            defaultNickname={config.nickname}
          />

          {/* Nome do Evento */}
          <div className="space-y-2">
            <Label>Nome do Evento *</Label>
            <Input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="sign_up"
            />
            <p className="text-xs text-muted-foreground">
              Evento disparado via <code>gtag('event', ...)</code>
            </p>
          </div>

          {/* Parâmetros */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Parâmetros (opcional)</Label>
              <Button variant="outline" size="sm" onClick={addParam}>
                <Plus className="h-3 w-3 mr-1" /> Adicionar
              </Button>
            </div>

            {params.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={p.key}
                  onChange={(e) => updateParam(i, 'key', e.target.value)}
                  placeholder="chave"
                  className="flex-1 h-8 text-sm"
                />
                <Input
                  value={p.value}
                  onChange={(e) => updateParam(i, 'value', e.target.value)}
                  placeholder="valor ou {{variavel}}"
                  className="flex-1 h-8 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => removeParam(i)} className="h-8 px-2">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}

            {params.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                Nenhum parâmetro adicionado
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!eventName.trim()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="tracking_ga4" />
    </>
  );
}